import type { ReactNode } from 'react';
import { InfoIcon } from '@phosphor-icons/react';
import { Badge } from '@voll-intelligence/ui-kit/ui/badge';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { Spinner } from '@voll-intelligence/ui-kit/ui/spinner';
import type {
  CheckoutBookedService,
  CheckoutSummaryTraveler,
} from '../summary/checkout_trip_summary.js';
import { PersonAvatar } from '../../shared/person/person_row.js';
import { RequestServiceRow, type RequestServiceRowItem } from './request_service_rows.js';

export interface RequestTripSummaryCopy {
  /** "Sua viagem". */
  kicker: string;
  /** Selo do pedido ("Em andamento"). */
  status: string;
  totalLabel: string;
  submit: string;
  submitting: string;
  back: string;
}

export interface RequestTripSummaryProps {
  /** "Viagem para Recife"; ausente ⇒ sem título. */
  title?: string;
  /** A janela do pedido, ao lado do título. */
  dates?: string;
  traveler?: CheckoutSummaryTraveler;
  /** O que a viagem JÁ tem, só-leitura, fora de qualquer conta. */
  booked: readonly CheckoutBookedService[];
  /** O que o pedido põe na viagem. */
  rows: readonly RequestServiceRowItem[];
  /** O total sem régua de preço ("A cotar", ou a ida guardada + "a cotar"). */
  total: string;
  /** A nota do produto embaixo do CTA. */
  note: string;
  submitting: boolean;
  canSubmit: boolean;
  /** Faixa âmbar do erro determinado. */
  notice?: string;
  onSubmit: () => void;
  /** Saída secundária do rodapé no touch — o ✕ do topbar fica longe do polegar. */
  onBack: () => void;
  copy: RequestTripSummaryCopy;
}

/**
 * O cartão "Sua viagem" do pedido à equipe. A mesma anatomia do cartão do checkout
 * online (título, meta, o que a viagem já tem, o que está sendo pedido, CTA e nota)
 * SEM a régua de preço: o item pedido entra tracejado, com "a cotar" onde o valor
 * iria.
 */
export function RequestTripSummary({
  title,
  dates,
  traveler,
  booked,
  rows,
  total,
  note,
  submitting,
  canSubmit,
  notice,
  onSubmit,
  onBack,
  copy,
}: Readonly<RequestTripSummaryProps>): ReactNode {
  return (
    <div className="genui2 bg-card border-border grid gap-3 rounded-xl border p-5 shadow-md">
      <div className="flex items-center justify-between gap-2">
        <span className="text-ui font-semibold">{copy.kicker}</span>
        <Badge variant="soft" color="neutral">
          {copy.status}
        </Badge>
      </div>
      {title && (
        <h3 className="text-title m-0 font-semibold">
          {title}
          {dates && <span className="text-muted-foreground font-normal"> · {dates}</span>}
        </h3>
      )}
      {traveler && (
        <p className="text-caption text-muted-foreground m-0 flex items-center gap-1.5 font-semibold">
          <PersonAvatar name={traveler.name} photoUrl={traveler.photoUrl} />
          {traveler.label}
        </p>
      )}
      <ul className="m-0 grid min-w-0 list-none gap-2 p-0">
        {booked.map((service) => (
          <li
            key={service.id}
            className="border-border bg-muted grid gap-0.5 rounded-lg border px-3 py-2"
          >
            <span className="text-caption font-medium">{service.title}</span>
            <span className="text-caption text-muted-foreground inline-flex gap-2">
              {service.status && <span>{service.status}</span>}
              {service.price && <span>{service.price}</span>}
            </span>
          </li>
        ))}
        {rows.map((row) => (
          <RequestServiceRow key={row.key} row={row} />
        ))}
      </ul>
      <div className="bg-border h-px" />
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-ui font-semibold">{copy.totalLabel}</span>
        <span className="text-ui font-bold">{total}</span>
      </div>
      <Button
        type="button"
        size="lg"
        className="w-full"
        disabled={!canSubmit || submitting}
        onClick={onSubmit}
      >
        {submitting && <Spinner aria-hidden />}
        {submitting ? copy.submitting : copy.submit}
      </Button>
      {notice && (
        <p
          className="text-caption m-0 rounded-lg bg-(--warning-soft) p-3 text-(--warning-strong)"
          role="alert"
        >
          {notice}
        </p>
      )}
      <Button
        type="button"
        variant="outline"
        size="lg"
        className="wide:hidden w-full"
        disabled={submitting}
        onClick={onBack}
      >
        {copy.back}
      </Button>
      <p className="text-caption text-muted-foreground bg-muted m-0 flex items-start gap-2 rounded-lg p-3">
        <InfoIcon size={14} className="mt-0.5 shrink-0" aria-hidden />
        <span>{note}</span>
      </p>
    </div>
  );
}
